const byTech20 = [
    {
      title: "Cómo verificar tu contrato en el explorador",
      value: `
  Antes de interactuar con tu contrato o compartirlo, es fundamental **verificar que el despliegue fue exitoso en la blockchain**. Esto garantiza que el contrato realmente está activo en la red seleccionada (por ejemplo, Sepolia) y listo para su uso.
  
  En esta sección aprenderás cómo confirmar que la transacción se completó correctamente usando MetaMask, y cómo validar que tu contrato ya está disponible para ser consultado e interactuado.
  
  ---
  
  **1. Abre el contrato en el explorador de bloques**

Después de desplegar tu contrato, puedes acceder a la dirección del contrato directamente en el explorador de bloques correspondiente (por ejemplo, SepoliaScan o Etherscan).  
Aquí podrás ver toda la información pública relacionada al contrato: su dirección, creador, transacciones, saldo y el rastreador del token si aplica.

En la imagen puedes ver la vista general del contrato desplegado en SepoliaScan:

![Vista general del contrato desplegado en SepoliaScan](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-3.png)

**2. Revisa el bytecode desplegado en la pestaña Contract**

Haz clic en la pestaña **Contract** dentro del explorador (SepoliaScan/Etherscan) para acceder a los detalles técnicos del contrato.  
Aquí podrás ver el **Bytecode** (código compilado) que fue cargado en la blockchain. Este es el código que realmente ejecuta la EVM y confirma que tu contrato fue desplegado correctamente.

Si deseas continuar con la verificación del contrato, usa la opción **Verify and Publish** para subir el código fuente y que otros usuarios puedan auditarlo y consultarlo de forma transparente.

En la imagen puedes ver el bytecode del contrato recién desplegado:

![Bytecode desplegado en la pestaña Contract](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-4.png)

**3. Inicia el proceso de verificación y publicación del contrato**

Haz clic en el enlace **Verify and Publish** dentro de la pestaña **Contract** del explorador.  
Se abrirá el formulario para verificar y publicar el código fuente de tu contrato.  
Aquí debes ingresar la **dirección del contrato** desplegado (por lo general, se autocompleta), seleccionar el tipo de compilador (**Solidity**) y la versión exacta utilizada en Remix IDE, así como la licencia de código abierto.

Este paso es fundamental para que cualquier persona pueda consultar el código fuente original y compararlo con el bytecode publicado, lo que incrementa la transparencia y seguridad de tu proyecto.

En la imagen puedes ver el formulario inicial de verificación:

![Formulario inicial de verificación de contrato en Etherscan](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-5.png)

**4. Completa los campos de compilador y licencia para la verificación**

En el formulario de verificación, selecciona cuidadosamente los siguientes datos:
- **Compiler Type:** Selecciona "Solidity (Single file)".
- **Compiler Version:** Elige la versión exacta que utilizaste en Remix IDE (por ejemplo, v0.8.30+commit.73712a01).
- **Open Source License Type:** Escoge la licencia de tu preferencia (por ejemplo, MIT License).

Asegúrate de que la dirección del contrato sea la correcta y acepta los términos del servicio marcando la casilla correspondiente.  
Después de revisar que todo esté correcto, haz clic en **Continue** para avanzar al siguiente paso de la verificación y publicación del código fuente.

En la imagen puedes ver cómo se deben completar los campos antes de continuar:

![Campos del compilador y licencia en el formulario de verificación de Etherscan](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-6.png)

**5. Configuración avanzada y argumentos del constructor**

En la sección de configuración avanzada, asegúrate de seleccionar las opciones adecuadas:
- **Optimization:** Si no usaste optimización al compilar en Remix, deja esta opción en "No".
- **Runs (Optimizer):** Déjalo como está si no usaste optimización.
- **EVM Version to target:** Elige "default (compiler defaults)" si no cambiaste esto en Remix.
- **License Type:** Debe coincidir con la que seleccionaste antes, por ejemplo, "MIT License (MIT)".

En la sección de **Constructor Arguments ABI-encoded**, deja el campo vacío si tu contrato no requiere argumentos en el constructor (como es típico en muchos ERC20 sencillos).  
Si tu contrato requiere parámetros al desplegar, aquí deberás pegarlos en formato ABI codificado.

En la imagen puedes ver cómo debe verse la configuración avanzada antes de continuar:

![Configuración avanzada y argumentos del constructor](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-10.png)

**6. Ingresa el código fuente del contrato para verificación**

En el siguiente paso, debes pegar el código fuente completo de tu contrato Solidity en el campo correspondiente.  
Asegúrate de que el código coincida exactamente con el que compilaste y desplegaste, incluyendo la versión del compilador y la licencia (por ejemplo, MIT).

Este proceso le permite al explorador (SepoliaScan o Etherscan) comparar el bytecode generado y mostrar el contrato como "verificado", permitiendo a cualquier persona revisar su código fuente.

En la imagen puedes ver dónde se pega el código fuente del contrato:

![Campo para ingresar el código fuente del contrato](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-11.png)

**7. Verifica que el campo para el código fuente esté vacío antes de pegar**

Antes de pegar tu contrato, asegúrate de que el campo para ingresar el código fuente esté completamente vacío.  
Esto previene errores de compilación por residuos de código anterior o espacios innecesarios.

Cuando veas el campo vacío como en la imagen, ahora sí puedes pegar el código fuente original de tu contrato Solidity y continuar con el proceso de verificación.

En la imagen puedes ver el campo listo para pegar el código fuente:

![Campo vacío para el código fuente en la verificación del contrato](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-12.png)

**8. Copia el Bytecode exacto del contrato desde el explorador**

Ahora, selecciona y copia todo el **Bytecode** que aparece en la pestaña **Contract** del explorador de bloques (SepoliaScan/Etherscan).  
Este bytecode es la versión compilada de tu contrato y deberás tenerlo listo en caso de necesitar verificar manualmente la equivalencia entre el código fuente y el código desplegado.

Asegúrate de **copiarlo completo** (puede ser muy largo) y sin espacios extra.  
Esto será útil para pasos avanzados de verificación, auditoría o recuperación del contrato en caso de que pierdas acceso al código fuente original.

En la imagen puedes ver cómo se selecciona y copia el Bytecode directamente del explorador:

![Copia el Bytecode del contrato en el explorador](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-14.png)


**9. Marca la casilla "No soy un robot" y haz clic en "Verify and Publish"**

Después de revisar que todos los campos estén completos, debes marcar la casilla **"No soy un robot"** para confirmar que no eres un bot automatizado.

Luego, haz clic en el botón azul **"Verify and Publish"** para enviar la verificación de tu contrato.  
Si todo está correcto, el explorador te mostrará un mensaje de éxito y tu contrato quedará verificado públicamente en la blockchain.

En la imagen puedes ver la casilla de verificación y el botón correspondiente:

![Verificar que no eres un robot y publicar el contrato](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-15.png)

**10. Confirmación de verificación exitosa**

Si todo ha sido correcto, el explorador mostrará un mensaje verde indicando que el Bytecode y el ABI coinciden para la dirección del contrato.  
Esto significa que tu contrato ha sido verificado y publicado correctamente.

En esta sección también puedes revisar información adicional como:
- El TxHash de la transacción que creó el contrato.
- El resultado del compilador y logs de depuración.

En la imagen puedes ver el mensaje de éxito y los detalles técnicos:

![Verificación exitosa del contrato en Etherscan](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-16.png)

**11. Revisión del código fuente verificado**

Una vez verificado el contrato, el explorador mostrará el código fuente completo del contrato en Solidity.  
Podrás revisar todas las funciones, eventos y variables públicas tal como están en el contrato desplegado.

También se muestran detalles importantes como:  
- Nombre del contrato  
- Versión del compilador utilizada  
- Configuración de optimización y licencia  

En la imagen puedes ver el código fuente verificado de ejemplo:

![Código fuente verificado del contrato](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-17.png)

**12. Detalles avanzados y metadatos del contrato verificado**

Después de verificar el código fuente, el explorador muestra información técnica adicional relevante para desarrolladores y auditores:  

- **Constructor Arguments (ABI-Encoded):** Son los parámetros codificados que se usaron durante el despliegue del contrato, si los hubiera.  
- **Deployed Bytecode Sourcemap:** Un mapa que vincula el bytecode desplegado con el código fuente para facilitar la depuración.  
- **Swarm Source:** Dirección IPFS o Swarm donde se aloja el código fuente descentralizado.  

Estos datos permiten un análisis más profundo del contrato, garantizando transparencia y trazabilidad completa.

En la imagen puedes observar un ejemplo de esta sección con todos los detalles técnicos:

![Detalles avanzados y metadatos del contrato](https://raw.githubusercontent.com/AppsDevsLeon/ImagesProject/refs/heads/main/25-18.png)

  `
  
  
    }
  ];
  
  export default byTech20;
  